import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, InputBase } from '@mui/material';
import { COLORS, FONT } from './tokens';
import { MaterialSymbol } from './MaterialSymbol';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: number;

  debounceMs?: number;
  fullWidth?: boolean;
}

export const SearchField = ({
  value,
  onChange,
  placeholder = 'Search',
  width = 320,
  debounceMs = 250,
  fullWidth = false,
}: SearchFieldProps): ReactElement => {
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setText(value);
  }, [value]);

  const commit = (next: string): void => {
    setText(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(next.trim()), debounceMs);
  };

  const clear = (): void => {
    setText('');
    if (timer.current) clearTimeout(timer.current);
    onChange('');
  };

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : `${width}px`,
        maxWidth: '100%',
        height: '48px',
        px: '12px',
        gap: '8px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
        backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.filterBorder}`,
        flexShrink: fullWidth ? 1 : 0,
        minWidth: 0,
        overflow: 'hidden',
        transition: 'border-color 150ms ease',
        '&:focus-within': { borderColor: COLORS.navy },
      }}
    >
      <MaterialSymbol
        name="search"
        size={18}
        color={COLORS.filterPlaceholder}
      />
      <InputBase
        value={text}
        onChange={e => commit(e.target.value)}
        placeholder={placeholder}
        sx={{
          flex: '1 0 0',
          minWidth: 0,
          fontFamily: FONT,
          fontWeight: 400,
          fontSize: '14px',
          color: COLORS.bodyDark,
          '& input::placeholder': {
            color: COLORS.filterPlaceholder,
            opacity: 1,
          },
        }}
      />
      {text.length > 0 && (
        <ButtonBase
          onClick={clear}
          disableRipple
          aria-label="Clear search"
          sx={{
            flexShrink: 0,
            borderRadius: '50%',
            p: '2px',
            color: COLORS.filterPlaceholder,
            '&:hover': { color: COLORS.navy },
            '&:focus-visible': {
              outline: `2px solid ${COLORS.navy}`,
              outlineOffset: '2px',
            },
          }}
        >
          <MaterialSymbol name="close" size={18} />
        </ButtonBase>
      )}
    </Box>
  );
};
